import { ErrorMessageWrapper } from "@/common/components/ErrorMessageWrapper"
import KanbanTable from "@/common/components/KanbanTable"
import ListFilters from "@/common/components/ListFilters"
import ListHeader from "@/common/components/ListHeader"
import TextField from "@/common/components/TextField"
import useDebounce from "@/common/hooks/useDebounce"
import { useFilterParams } from '@/common/hooks/useFilterParams'
import { Mapper } from "@/common/services/mapper"
import { UserData } from "@/common/types/users"
import { columnStateMapping } from "@/features/publicCompanyLeadsFunnel/constants/columnStateMapping.constant"
import { stateOptions } from "@/features/publicCompanyLeadsFunnel/constants/stateOptions.constant"
import { usePublicCompanyLeadsInfinite } from "@/features/publicCompanyLeadsFunnel/hooks/usePublicCompanyLeadsInfinite"
import { Building, Search } from "lucide-react"
import { useNavigate } from "react-router-dom"
import FilterCheckbox from "../../../../common/components/FilterCheckbox/"
import CreateModal from "../../components/CreateModal"
import { usePublicCompanyLeadRelatedUsers } from "../../hooks/usePublicCompanyLeadRelatedUsers"
import { PublicCompanyLeadData } from "../../types/publicCompanyLead"

const List = () => {
  const navigate = useNavigate()
  const { filterParams, setFilterParams } = useFilterParams()
  const [searchLeadName, setSearchLeadName] = useDebounce(filterParams.leadName || '')
  const [searchCreator, setSearchCreator] = useDebounce('')

  const {
    data: registeredData = [],
    isPending: isPendingRegistered,
    hasNextPage: hasNextPageRegistered,
    isFetchingNextPage: isFetchingNextPageRegistered,
    fetchNextPage: fetchNextPageRegistered,
    error: registeredError = {} as any,
    refetch: refetchRegistered,
  } = usePublicCompanyLeadsInfinite<PublicCompanyLeadData[]>({
    filters: {
      search: searchLeadName || '',
      state: filterParams.state?.value ? [filterParams.state.value] : columnStateMapping.registered,
      creatorId: filterParams.creator?.value || '',
      showOnlyArchived: filterParams.archived === 'true',
    },
    options: {
      enabled: !filterParams.state?.value || columnStateMapping.registered.includes(filterParams.state.value),
    },
  })

  const {
    data: qualifyingData = [],
    isPending: isPendingQualifying,
    hasNextPage: hasNextPageQualifying,
    isFetchingNextPage: isFetchingNextPageQualifying,
    fetchNextPage: fetchNextPageQualifying,
    refetch: refetchQualifying,
  } = usePublicCompanyLeadsInfinite<PublicCompanyLeadData[]>({
    filters: {
      search: searchLeadName || '',
      state: filterParams.state?.value ? [filterParams.state.value] : columnStateMapping.qualifying,
      creatorId: filterParams.creator?.value || '',
      showOnlyArchived: filterParams.archived === 'true',
    },
    options: {
      enabled: !filterParams.state?.value || columnStateMapping.qualifying.includes(filterParams.state.value),
    },
  })

  const {
    data: certameData = [],
    isPending: isPendingCertame,
    hasNextPage: hasNextPageCertame,
    isFetchingNextPage: isFetchingNextPageCertame,
    fetchNextPage: fetchNextPageCertame,
    refetch: refetchCertame,
  } = usePublicCompanyLeadsInfinite<PublicCompanyLeadData[]>({
    filters: {
      search: searchLeadName || '',
      state: filterParams.state?.value ? [filterParams.state.value] : columnStateMapping.certame,
      creatorId: filterParams.creator?.value || '',
      showOnlyArchived: filterParams.archived === 'true',
    },
    options: {
      enabled: !filterParams.state?.value || columnStateMapping.certame.includes(filterParams.state.value),
    },
  })

  const {
    data: negotiatingData = [],
    isPending: isPendingNegotiating,
    hasNextPage: hasNextPageNegotiating,
    isFetchingNextPage: isFetchingNextPageNegotiating,
    fetchNextPage: fetchNextPageNegotiating,
    refetch: refetchNegotiating,
  } = usePublicCompanyLeadsInfinite<PublicCompanyLeadData[]>({
    filters: {
      search: searchLeadName || '',
      state: filterParams.state?.value ? [filterParams.state.value] : columnStateMapping.negotiating,
      creatorId: filterParams.creator?.value || '',
      showOnlyArchived: filterParams.archived === 'true',
    },
    options: {
      enabled: !filterParams.state?.value || columnStateMapping.negotiating.includes(filterParams.state.value),
    },
  })

  const {
    data: implantationData = [],
    isPending: isPendingImplantation,
    hasNextPage: hasNextPageImplantation,
    isFetchingNextPage: isFetchingNextPageImplantation,
    fetchNextPage: fetchNextPageImplantation,
    refetch: refetchImplantation,
  } = usePublicCompanyLeadsInfinite<PublicCompanyLeadData[]>({
    filters: {
      search: searchLeadName || '',
      state: filterParams.state?.value ? [filterParams.state.value] : columnStateMapping.implantation,
      creatorId: filterParams.creator?.value || '',
      showOnlyArchived: filterParams.archived === 'true',
    },
    options: {
      enabled: !filterParams.state?.value || columnStateMapping.implantation.includes(filterParams.state.value),
    },
  })

  const { data: users } = usePublicCompanyLeadRelatedUsers({
    filters: {
      search: searchCreator,
    },
    mapper: Mapper.mapToOptions<UserData>({ labelFieldName: 'full_name', valueFieldName: 'id' }),
  })

  const refetchAll = () => {
    refetchRegistered()
    refetchQualifying()
    refetchCertame()
    refetchNegotiating()
    refetchImplantation()
  }

  return (
    <div className="flex flex-col gap-6">
      <ListHeader Icon={<Building className="w-10 h-10" strokeWidth={1.5} />} title="Funil de clientes públicos" />

      <ErrorMessageWrapper
        className="h-[calc(100vh-128px)]"
        statusCode={registeredError?.statusCode}
        refetchFunction={refetchAll}
      >
        <TextField
          LeftIcon={<Search />}
          placeholder="Pesquisar"
          className="rounded-sm shadow-[4px_0px_16px_0px_rgba(0,_0,_0,_0.5)]"
          value={filterParams.leadName || ''}
          onChange={(e: any) => {
            setSearchLeadName(e.target?.value)
            setFilterParams((prev) => ({
              ...prev,
              leadName: e.target?.value,
            }))
          }}
        />

        <div className="flex justify-between">
          <ListFilters
            filters={[
              {
                className: 'min-w-40',
                onChange: (e: any) => {
                  setFilterParams((prev) => ({
                    ...prev,
                    state: e.value ? e : undefined,
                  }))
                },
                value: filterParams.state || undefined,
                options: [
                  { label: 'Todos', value: '' },
                  ...stateOptions.filter((option) => option.value !== 'completed'),
                ],
                placeholder: 'Estado',
                dataTestid: 'state_filter',
              },
              {
                className: '',
                onInputChange: (inputValue) => setSearchCreator(inputValue),
                onChange: (e: any) => {
                  setFilterParams((prev) => ({
                    ...prev,
                    creator: e.value ? e : undefined,
                  }))
                },
                value: filterParams.creator || undefined,
                options: [{ label: 'Todos', value: '' }, ...(users || [])],
                placeholder: 'Responsável',
                dataTestid: 'creator_filter',
              },
            ]}
          />

          <FilterCheckbox
            isChecked={filterParams.archived === 'true'}
            onChange={(value) => {
              setFilterParams((prev) => ({
                ...prev,
                archived: value,
              }))
            }}
            label="Exibir apenas Arquivados"
            id="filter-checkbox"
            name="filter-checkbox"
          />
        </div>

        <div className="max-h-[calc(100vh-188px-4.5rem)]">
          <KanbanTable
            columns={[
              {
                title: 'Registrados',
                cards:
                  !filterParams.state?.value || columnStateMapping.registered.includes(filterParams.state?.value)
                    ? registeredData
                    : [],
                headerButton: <CreateModal />,
                onClickOnCard: (id) => navigate(`/public-clients/${id}/edit`),
                isPending: isPendingRegistered,
                hasNextPage: hasNextPageRegistered,
                isFetchingNextPage: isFetchingNextPageRegistered,
                fetchNextPage: fetchNextPageRegistered,
                skeletonCount: 3,
                dataTestid: 'registered_state',
              },
              {
                title: 'Qualificando',
                cards:
                  !filterParams.state?.value || columnStateMapping.qualifying.includes(filterParams.state?.value)
                    ? qualifyingData
                    : [],
                onClickOnCard: (id) => navigate(`/public-clients/${id}/edit`),
                isPending: isPendingQualifying,
                hasNextPage: hasNextPageQualifying,
                isFetchingNextPage: isFetchingNextPageQualifying,
                fetchNextPage: fetchNextPageQualifying,
                skeletonCount: 4,
                dataTestid: 'qualifying_state',
              },
              {
                title: 'Certame',
                cards:
                  !filterParams.state?.value || columnStateMapping.certame.includes(filterParams.state?.value)
                    ? certameData
                    : [],
                onClickOnCard: (id) => navigate(`/public-clients/${id}/edit`),
                isPending: isPendingCertame,
                hasNextPage: hasNextPageCertame,
                isFetchingNextPage: isFetchingNextPageCertame,
                fetchNextPage: fetchNextPageCertame,
                skeletonCount: 3,
              },
              {
                title: 'Contratação',
                cards:
                  !filterParams.state?.value || columnStateMapping.negotiating.includes(filterParams.state?.value)
                    ? negotiatingData
                    : [],
                onClickOnCard: (id) => navigate(`/public-clients/${id}/edit`),
                isPending: isPendingNegotiating,
                hasNextPage: hasNextPageNegotiating,
                isFetchingNextPage: isFetchingNextPageNegotiating,
                fetchNextPage: fetchNextPageNegotiating,
                skeletonCount: 3,
              },
              {
                title: 'Implantação',
                cards:
                  !filterParams.state?.value || columnStateMapping.implantation.includes(filterParams.state?.value)
                    ? implantationData
                    : [],
                onClickOnCard: (id) => navigate(`/public-clients/${id}/edit`),
                isPending: isPendingImplantation,
                hasNextPage: hasNextPageImplantation,
                isFetchingNextPage: isFetchingNextPageImplantation,
                fetchNextPage: fetchNextPageImplantation,
                skeletonCount: 4,
              },
            ]}
          />
        </div>
      </ErrorMessageWrapper>
    </div>
  )
}

export default List
